import { useApp } from '../store'

/**
 * lib/persistedState.ts — everything sign-out has to forget.
 *
 * ── THE BUG THIS FIXES ────────────────────────────────────────────────────
 * The Zustand store persists under the literal key `aska-platform-state`
 * (`web/src/store/index.ts`) and its `partialize` keeps `cartItems`, the last
 * 50 `orders` and the last 50 `invoices`. That key is NOT scoped by uid, and
 * `signOut()` did not touch it. On a shared org workstation — a trade desk
 * where three people use one browser profile, which is exactly the population
 * org accounts is for — the next person to sign in inherited the previous
 * person's cart, order history and INVOICES, straight out of localStorage,
 * rendered before any network call could correct it. Order and invoice rows
 * carry prices, which invariant 3 says are visible only to a signed-in,
 * KYC-approved caller. This is a data-leak-shaped bug, not a tidiness one.
 *
 * ── WHY BOTH HALVES ARE NEEDED ────────────────────────────────────────────
 * Deleting the localStorage key alone is not enough: the store is ALREADY
 * hydrated in memory, and a SPA sign-out does not reload the page. Zustand
 * would then re-persist the in-memory cart on the next `set()`. So we reset
 * the slices first and clear the storage second.
 *
 * ── SCOPE: NON-UID-SCOPED SIGNED-IN STATE ONLY ────────────────────────────
 * Cleared: the app store, the two "recent searches" lists, notification
 * read-marks, the overdue-invoice snooze, and the DEPARTING uid's chat
 * transcript. All of those are one person's activity under a shared key (or,
 * for chat, under a key we can name precisely because we still hold the uid).
 *
 * Deliberately NOT cleared:
 *  · `diaex.deviceId` — device identity, not user identity. Wiping it would
 *    mint a brand-new device on every sign-out and burn all three slots of the
 *    3-device cap within a day (docs/ORG-ACCOUNTS.md decision 3).
 *  · `aska:feedback`, `aska:services`, `aska:searchView2`, `m-browse-view`,
 *    `aska.appMode` — device/browser preferences. A shared workstation SHOULD
 *    keep the sound setting and the table-vs-cards choice.
 *  · the `anon` chat scope — signing out returns you to anon; taking that
 *    thread away too is a loss with no privacy benefit.
 *  · the signup wizard's `sessionStorage` draft — clearing it mid-flow would
 *    strand someone who signed out of a stale session while enrolling.
 */

/** The app store's persist key, mirrored from `web/src/store/index.ts`. */
export const APP_STATE_KEY = 'aska-platform-state'

/** Shared keys holding one signed-in person's activity. */
const SHARED_ACTIVITY_KEYS = [
  APP_STATE_KEY,
  'aska:recentSearches',       // mobile/lib/recentSearches.ts
  'aska-spotlight-recent',     // components/Spotlight.tsx
  'aska.notifications.read',   // {mobile/screens/account,routes}/notifications
  'aska.overdue.snoozeUntil',  // components/OverdueGuard.tsx
] as const

/** Per-uid chat transcript key (routes/Chat.tsx + mobile useGemmaChat.ts). */
const CHAT_PREFIX = 'aska:chat:v2:'

function drop(key: string): void {
  try { window.localStorage.removeItem(key) } catch { /* private mode — nothing persisted anyway */ }
}

/**
 * Wipe the in-memory app state AND its persisted mirror. Call this from
 * `signOut()` BEFORE the Firebase sign-out (so `uid` is still readable), and
 * never anywhere else — this is destructive by design.
 *
 * Every step is independently failure-swallowed: sign-out must complete even
 * if storage is unavailable, or a user on a shared machine would be stuck
 * signed in, which is a strictly worse outcome than a stale cart.
 */
export function clearPersistedAppState(opts: { uid?: string | null } = {}): void {
  // 1. In-memory first. The store is live; a later `set()` would re-persist.
  try {
    useApp.setState({
      user: null,
      terms: null,
      signedIn: false,
      cartItems: [],
      orders: [],
      invoices: [],
      searchResults: [],
      freshness: {},
    })
  } catch { /* store not initialised (SSR / unit test) */ }

  // 2. The persisted mirror, via zustand's own API so its internal
  //    hydration bookkeeping stays consistent, then the raw key as a belt.
  try { useApp.persist?.clearStorage?.() } catch { /* no persist middleware */ }
  for (const key of SHARED_ACTIVITY_KEYS) drop(key)

  // 3. The departing user's chat transcript. Named exactly, so the `anon`
  //    scope and any other user's scope survive untouched.
  if (opts.uid) drop(`${CHAT_PREFIX}${opts.uid}`)
}
